import { channelCustomTypes } from '@inbox-demo-nx/app-v2-ondemand-constants';
import { channelMetaDataKeys } from '@inbox-demo-nx/constants';
import {
  useAuthContext,
  useScenarioContext,
  useSendBirdContext,
} from '@inbox-demo-nx/contexts';
import useNewMessageNotification from '@inbox-demo-nx/hooks-use-new-message-notification';
import useScenarioUtils from '@inbox-demo-nx/hooks-use-scenario-utils';
import { ColStack, RowStack, Spacer } from '@inbox-demo-nx/ui';
import { useAnimatingCircleContext } from '@inbox-demo-nx/ui-animating-circle';
import Button from '@inbox-demo-nx/ui-button';
import Image from '@inbox-demo-nx/ui-image';
import OpacityPressable from '@inbox-demo-nx/ui-opacity-pressable';
import analytics from '@inbox-demo-nx/utils-analytics';
import {
  getMessagePreviewText,
  useGetChannelTitle,
} from '@inbox-demo-nx/utils-chat-common';
import { parseMessageData } from '@inbox-demo-nx/utils-data-utils';
import {
  NavigationProp,
  useFocusEffect,
  useNavigation,
  useNavigationState,
} from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { ScrollView, Text, useSx, View } from 'dripsy';
import { useCallback, useState } from 'react';
import { Fragment, useEffect, useRef } from 'react';
import { Platform, SafeAreaView, StatusBar } from 'react-native';

import ArrowLeftIcon from '../../../assets/icon-arrow-left.svg';
import ChatIcon from '../../../assets/icon-chat.svg';

const tabs = ['Sushi Box', 'Nigiri', 'Sashimi', 'Donburi'];

const menu = [
  {
    name: 'Sushi Box. Sashimi Moriwase',
    description: '8 Pieces Chef choice Sashimi & California Roll with soup',
    price: '$60.00',
    imageSource: require('../../../assets/img-thumbnail-sushi-01.webp'),
  },
  {
    name: 'Sushi Box Salmon, Tuna Roll',
    description:
      'Seared Yellowtail Nigiri 2pcs,\nSalmon Nigiri 2 pcs & Spicy Tuna ...',
    price: '$50.00',
    imageSource: require('../../../assets/img-thumbnail-sushi-02.webp'),
  },
  {
    name: 'Sushi Box. Sushi Moriwase',
    description: '5 Pieces Chef Choice Nigiri & California Roll with miso soup',
    price: '$45.00',
    imageSource: require('../../../assets/img-thumbnail-sushi-03.webp'),
  },
  {
    name: 'Sushi Box Salmon, CA Roll',
    description:
      'Salmon Nigiri 2pcs, Albacore Nigiri 2 pcs & California Cut Roll with soup',
    price: '$40.00',
    imageSource: require('../../../assets/img-thumbnail-sushi-04.webp'),
  },
];

const HomeScreen = () => {
  const navigation = useNavigation<NavigationProp<any>>();
  const { sendbird } = useSendBirdContext();
  const { currentUser } = useAuthContext();
  const scenario = useScenarioContext();
  const currentRoute = useNavigationState((state) => state.routes[state.index]);
  const currentRouteRef = useRef(currentRoute);
  const showNewMessageNotification = useNewMessageNotification();
  const getChannelTitle = useGetChannelTitle();
  const { setChannelState } = useScenarioUtils();
  const currentUserId = currentUser?.userId;
  const [, setDidStartDemo] = useState(false);
  const sx = useSx();
  const { setTargetId } = useAnimatingCircleContext();

  useFocusEffect(
    useCallback(() => {
      setTargetId('startDemoButton');
      return () => {
        setTargetId('');
      };
    }, [setTargetId])
  );

  useEffect(() => {
    currentRouteRef.current = currentRoute;
  }, [currentRoute]);

  const { data: notificationChannel, refetch } = useQuery(
    ['notificationChannel', currentUserId],
    async (context) => {
      const [, userId] = context.queryKey;
      if (!userId) return { url: '', state: '' };

      const channelQuery =
        sendbird.GroupChannel.createMyGroupChannelListQuery();
      channelQuery.customTypesFilter = [channelCustomTypes.notifications];
      channelQuery.includeEmpty = true;
      const [channel] = await channelQuery.next();

      if (channel) {
        const metadata = await channel.getAllMetaData();
        const currentState = metadata[channelMetaDataKeys.state];
        return { url: channel.url, state: currentState };
      }

      return { url: '', state: '' };
    },
    { enabled: !!currentUserId, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    const channelHandler = new sendbird.ChannelHandler();
    channelHandler.onMessageReceived = (channel, message) => {
      if (!channel.isGroupChannel()) return;

      if (currentRouteRef.current.name === 'Inbox') {
        return;
      }

      if (
        currentRouteRef.current.name === 'Chat' &&
        (currentRouteRef.current.params as any)?.['channelUrl'] === channel.url
      ) {
        return;
      }

      if (getMessagePreviewText(message) === 'Typing...') return;

      showNewMessageNotification({
        title: getChannelTitle(channel),
        description: getMessagePreviewText(message),
        actions: parseMessageData(message.data)?.notificationActions,
        targetChannelCustomType: channel.customType || '',
      });
    };
    sendbird.addChannelHandler('HOME', channelHandler);
    return () => {
      sendbird.removeChannelHandler('HOME');
    };
  }, [getChannelTitle, sendbird, showNewMessageNotification]);

  const startDemo = async () => {
    analytics.track('start_demo', { demo_key: scenario.key });

    setDidStartDemo(true);

    if (!notificationChannel || !notificationChannel.url || !currentUserId) {
      return;
    }

    if (notificationChannel.state !== 'initial') {
      navigation.navigate('Inbox');
      return;
    }

    await setChannelState({
      channelUrl: notificationChannel.url,
      state: 'start',
      userId: currentUserId,
    });
    refetch();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: 'white' }}>
      <StatusBar barStyle="dark-content" />
      <ColStack style={{ alignItems: 'stretch' }}>
        <RowStack
          style={{
            height: 44,
            backgroundColor: 'white',
            paddingHorizontal: 16,
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          <Text
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              textAlign: 'center',
              variant: 'styles.textMedium',
              fontWeight: '700',
            }}
          >
            Sushi Son
          </Text>
          <ArrowLeftIcon />
          <OpacityPressable onPress={() => navigation.navigate('Inbox')}>
            <ChatIcon />
          </OpacityPressable>
        </RowStack>
        <RowStack
          style={sx({ px: 16, borderBottomWidth: 1, borderColor: '#E0E0E0' })}
        >
          {tabs.map((tab, index) => (
            <View
              key={tab}
              sx={{
                position: 'relative',
                mr: 24,
                height: 40,
                justifyContent: 'center',
              }}
            >
              <Text
                sx={{
                  variant: 'styles.textSmedium',
                  fontWeight: '600',
                  color: index === 0 ? 'text' : 'secondaryText',
                }}
              >
                {tab}
              </Text>
              {index === 0 && (
                <View
                  sx={{
                    backgroundColor: 'accent',
                    height: 2,
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: -1,
                  }}
                />
              )}
            </View>
          ))}
        </RowStack>
      </ColStack>
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        sx={{ flex: 1 }}
        contentContainerSx={{
          width: 375,
          mx: 'auto',
          padding: 16,
          paddingTop: 0,
        }}
      >
        <View
          style={{
            position: 'relative',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Image
            source={require('../../../assets/header-image.webp')}
            style={{ width: 375, height: 200 }}
          />
          <Button
            animatingCircleId="startDemoButton"
            title="Click to order"
            onPress={startDemo}
            rounded
            style={{
              position: 'absolute',
              bottom: 24,
              alignSelf: 'center',
            }}
            buttonStyle={sx({ px: 40 })}
          />
        </View>
        {menu.map((item, index) => (
          <Fragment key={item.name}>
            {index > 0 && (
              <View sx={{ backgroundColor: '#E0E0E0', height: 1, my: 16 }} />
            )}
            <RowStack>
              <ColStack style={sx({ flex: 1, py: 4 })}>
                <Text
                  sx={{
                    fontSize: 'smedium',
                    lineHeight: 16,
                    fontWeight: '700',
                  }}
                >
                  {item.name}
                </Text>
                <Text sx={{ variant: 'styles.textSmedium', mt: 8 }}>
                  {item.description}
                </Text>
                <Text
                  sx={{
                    mt: 12,
                    variant: 'styles.textMedium',
                    fontWeight: '500',
                  }}
                >
                  {item.price}
                </Text>
              </ColStack>
              <Spacer size={8} />
              <Image
                source={item.imageSource}
                style={{ width: 96, height: 96 }}
              />
            </RowStack>
          </Fragment>
        ))}
        <Spacer size={8} />
        {Platform.OS === 'web' && window.parent === window.self && (
          <Button
            title="Settings"
            variant="secondary"
            onPress={() => {
              navigation.navigate('Settings');
            }}
            style={{ margin: 8 }}
          />
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

export default HomeScreen;
